import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm } from "react-hook-form";
import { money } from "@/plan-logic/lib/format";
import {
  jobDefaults,
  jobRun,
  jobSchema,
  type JobValues,
  newOffer,
} from "@/plan-logic/decisions/values";
import { usePlanCtx } from "../PlanPage";
import { DateInput, Field, MoneyInput, RunButton, TextInput, useEdits } from "./fields";
import type { ToolFormProps } from "./forms";

type JobFormValues = JobValues;

/**
 * "What happens if my income changes?": your jobs today (keep, a new salary, or end), beside the
 * offers you're weighing. Salaries are amounts, not percentages (v43); a gap between jobs is the old
 * job's last day to the new one's start.
 */
export function JobToolForm({ onRun, busy, defaults, onEdit, ran }: ToolFormProps) {
  const { family, index, today } = usePlanCtx();
  const current = family.persons.flatMap((p) => p.jobs.map((j) => ({ person: p, job: j })));
  const saved = defaults as Partial<JobFormValues> | undefined;
  const { register, handleSubmit, watch, control, formState } = useForm<JobFormValues>({
    resolver: zodResolver(jobSchema),
    defaultValues: jobDefaults(family, today, saved),
  });
  const offers = useFieldArray({ control, name: "offers" });
  const e = formState.errors;
  useEdits(watch, onEdit);
  const existing = watch("existing");

  return (
    <form
      className="act-body dz-form"
      noValidate
      onSubmit={handleSubmit((v) => {
        const run = jobRun(v, family, today);
        onRun(run.changes, run.inputs);
      })}
    >
      <h4 className="dz-sub">What you do today</h4>
      {current.length === 0 && <p className="mut">There are no jobs in your plan yet.</p>}
      {current.map(({ person, job }, i) => (
        <div className="rowbox" key={job.uuid}>
          <div className="rowhead">
            <b>
              {index.person(person.uuid) || "You"} &middot; {job.title || job.employer || "Job"}
            </b>
            <span className="mut" style={{ marginLeft: "auto" }}>
              {money(job.preTaxAnnualIncome)} a year
            </span>
          </div>
          <div className="two">
            <Field label="In this scenario" htmlFor={`jb-act-${i}`}>
              <select id={`jb-act-${i}`} {...register(`existing.${i}.action`)}>
                <option value="keep">Keep it as it is</option>
                <option value="change">The salary changes</option>
                <option value="end">It ends</option>
              </select>
            </Field>
            {existing?.[i]?.action === "change" && (
              <Field
                label="New salary, before tax, a year"
                error={e.existing?.[i]?.income}
                htmlFor={`jb-inc-${i}`}
              >
                <MoneyInput id={`jb-inc-${i}`} reg={register(`existing.${i}.income`)} />
              </Field>
            )}
            {existing?.[i]?.action === "end" && (
              <Field label="Last day" error={e.existing?.[i]?.endDate} htmlFor={`jb-end-${i}`}>
                <DateInput id={`jb-end-${i}`} reg={register(`existing.${i}.endDate`)} />
              </Field>
            )}
          </div>
        </div>
      ))}

      <h4 className="dz-sub">The offer</h4>
      {offers.fields.map((f, i) => (
        <div className="rowbox" key={f.id}>
          <div className="rowhead">
            <b>Offer {i + 1}</b>
            <button type="button" className="rm" onClick={() => offers.remove(i)}>
              Remove
            </button>
          </div>
          <div className="two">
            <Field label="Role" error={e.offers?.[i]?.title} htmlFor={`of-t-${i}`}>
              <TextInput id={`of-t-${i}`} reg={register(`offers.${i}.title`)} />
            </Field>
            <Field label="Employer (optional)" htmlFor={`of-e-${i}`}>
              <TextInput id={`of-e-${i}`} reg={register(`offers.${i}.employer`)} />
            </Field>
          </div>
          <div className="three">
            <Field label="Whose" error={e.offers?.[i]?.personUuid} htmlFor={`of-p-${i}`}>
              <select id={`of-p-${i}`} {...register(`offers.${i}.personUuid`)}>
                {family.persons.map((p) => (
                  <option key={p.uuid} value={p.uuid}>
                    {index.person(p.uuid) || "You"}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Salary before tax, a year"
              error={e.offers?.[i]?.income}
              htmlFor={`of-i-${i}`}
            >
              <MoneyInput id={`of-i-${i}`} reg={register(`offers.${i}.income`)} />
            </Field>
            <Field label="Starts" error={e.offers?.[i]?.startDate} htmlFor={`of-s-${i}`}>
              <DateInput id={`of-s-${i}`} reg={register(`offers.${i}.startDate`)} />
            </Field>
          </div>
        </div>
      ))}
      <button type="button" className="mini" onClick={() => offers.append(newOffer(family, today))}>
        + Add an offer
      </button>
      {e.offers?.root?.message || (e.offers as { message?: string } | undefined)?.message ? (
        <p className="dz-err" role="alert">
          {e.offers?.root?.message ?? (e.offers as { message?: string }).message}
        </p>
      ) : null}
      <p className="hint">
        Taking a new job usually means ending the old one. Set that above so the plan doesn&rsquo;t
        count both. A gap between jobs is the time from the old one&rsquo;s last day to the new
        one&rsquo;s start.
      </p>
      <RunButton busy={busy} ran={ran} />
    </form>
  );
}
